import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type {
  CreatePromotionBody,
  PersonalCoupon,
  PersonalCouponListItem,
  Promotion,
  PromotionSuccess,
  UpdatePromotionBody,
} from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '@/lib/analytics/umami-client';

export type PromotionListStatus = 'active' | 'all';

const PROMOTIONS_PATH = '/api/restaurants/{restaurantId}/promotions';
const PROMOTION_PATH = '/api/restaurants/{restaurantId}/promotions/{promotionId}';
const COUPONS_PATH = '/api/restaurants/{restaurantId}/promotions/{promotionId}/personal-coupons';

const listKey = (restaurantId: string, status: PromotionListStatus) =>
  ['promotions', restaurantId, status] as const;
const oneKey = (restaurantId: string, promotionId: string) =>
  ['promotion', restaurantId, promotionId] as const;
const couponsKey = (restaurantId: string, promotionId: string) =>
  ['promotion-coupons', restaurantId, promotionId] as const;

export function usePromotions(status: PromotionListStatus, enabled = true) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: listKey(restaurantId, status),
    queryFn: async () => {
      const r = await openapiClient.GET(PROMOTIONS_PATH, {
        params: { path: { restaurantId }, query: { status } },
      });
      return unwrapResult<Promotion[]>(r);
    },
    staleTime: 0,
    enabled: !!restaurantId && enabled,
  });
}

export function usePromotion(promotionId: string | null) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: oneKey(restaurantId, promotionId ?? ''),
    queryFn: async () => {
      const r = await openapiClient.GET(PROMOTION_PATH, {
        params: { path: { restaurantId, promotionId: promotionId ?? '' } },
      });
      return unwrapResult<Promotion>(r);
    },
    enabled: !!restaurantId && !!promotionId,
  });
}

/** The issued-coupon history for a PRIVATE promotion (discount detail page). */
export function usePromotionCoupons(promotionId: string | null) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: couponsKey(restaurantId, promotionId ?? ''),
    queryFn: async () => {
      const r = await openapiClient.GET(COUPONS_PATH, {
        params: { path: { restaurantId, promotionId: promotionId ?? '' }, query: { lang: 'en' } },
      });
      return unwrapResult<PersonalCouponListItem[]>(r);
    },
    enabled: !!restaurantId && !!promotionId,
  });
}

export function useCreatePromotion() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePromotionBody) => {
      const r = await openapiClient.POST(PROMOTIONS_PATH, {
        params: { path: { restaurantId } },
        body,
      });
      return unwrapResult<Promotion>(r);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions', restaurantId] });
      trackEvent('setup_changed', { area: 'promo' });
    },
  });
}

export function useUpdatePromotion() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      promotionId,
      data,
    }: {
      promotionId: string;
      data: UpdatePromotionBody;
    }) => {
      const r = await openapiClient.PATCH(PROMOTION_PATH, {
        params: { path: { restaurantId, promotionId } },
        body: data,
      });
      return unwrapResult<Promotion>(r);
    },
    // Invalidate BOTH the list and the single-record cache the edit form reads
    // (oneKey) - without the latter, reopening the editor shows stale fields for
    // the 5-min staleTime, which reads as "my save didn't stick".
    onSuccess: (_res, { promotionId }) => {
      queryClient.invalidateQueries({ queryKey: ['promotions', restaurantId] });
      queryClient.invalidateQueries({ queryKey: oneKey(restaurantId, promotionId) });
      trackEvent('setup_changed', { area: 'promo' });
    },
  });
}

export function useArchivePromotion() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (promotionId: string) => {
      const r = await openapiClient.DELETE(PROMOTION_PATH, {
        params: { path: { restaurantId, promotionId } },
      });
      return unwrapResult<PromotionSuccess>(r);
    },
    onSuccess: (_res, promotionId) => {
      queryClient.invalidateQueries({ queryKey: ['promotions', restaurantId] });
      queryClient.invalidateQueries({ queryKey: oneKey(restaurantId, promotionId) });
      trackEvent('setup_changed', { area: 'promo' });
    },
  });
}

export function useIssuePersonalCoupon() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      promotionId,
      customerId,
      code,
      isSharable,
    }: {
      promotionId: string;
      customerId: string;
      code: string;
      isSharable?: boolean;
    }) => {
      const r = await openapiClient.POST(COUPONS_PATH, {
        params: { path: { restaurantId, promotionId } },
        body: { customerId, code, isSharable },
      });
      return unwrapResult<PersonalCoupon>(r);
    },
    // Refresh both surfaces that list this coupon: the promotion's issued-coupon
    // history and the recipient's coupon wallet on the customer record.
    onSuccess: (_res, { promotionId, customerId }) => {
      queryClient.invalidateQueries({ queryKey: ['promotions', restaurantId] });
      queryClient.invalidateQueries({ queryKey: couponsKey(restaurantId, promotionId) });
      queryClient.invalidateQueries({ queryKey: ['customer-coupons', customerId] });
    },
  });
}
